const ALLOWED_WIDTHS = [80, 100, 140, 200, 300, 400, 600, 700, 800, 1000, 1200, 1600]

function getClosestWidth(targetWidth) {
    const numericWidth = parseInt(targetWidth, 10)
    if (isNaN(numericWidth) || numericWidth <= 0) return 400
    return ALLOWED_WIDTHS.reduce((prev, curr) => {
        return Math.abs(curr - numericWidth) < Math.abs(prev - numericWidth) ? curr : prev
    })
}

export function getImageUrl(path) {
    if (!path) return ''
    if (path.startsWith('http')) return path
    if (path.startsWith('/uploads')) {
        return path
    }
    return path
}

export function getOptimizedImageUrl(url, width) {
    if (!url || typeof url !== 'string') return ''
    if (!width) return url
    if (url.includes('?') || url.includes('/uploads/cache/')) return url

    // Handle relative uploads missing leading slash: e.g. "uploads/image.jpg"
    let cleanUrl = url
    if (cleanUrl.startsWith('uploads/')) {
        cleanUrl = '/' + cleanUrl
    }

    // Handle full domain URLs or relative paths containing /uploads/
    const uploadIndex = cleanUrl.indexOf('/uploads/')
    if (uploadIndex === -1) return url

    const targetW = getClosestWidth(width)
    const prefix = cleanUrl.substring(0, uploadIndex + 9) // ".../uploads/"
    const rest = cleanUrl.substring(uploadIndex + 9) // "filename.ext" or "sub/filename.ext"

    const lastSlash = rest.lastIndexOf('/')
    let subfolder = ''
    let filename = rest

    if (lastSlash !== -1) {
        subfolder = rest.substring(0, lastSlash + 1)
        filename = rest.substring(lastSlash + 1)
    }

    const dotIndex = filename.lastIndexOf('.')
    if (dotIndex === -1) {
        return `${cleanUrl}?w=${targetW}`
    }

    const baseName = filename.substring(0, dotIndex)
    const ext = filename.substring(dotIndex + 1)

    // Ignore non-scalable formats
    if (['svg', 'gif', 'ico'].includes(ext.toLowerCase())) {
        return cleanUrl
    }

    return `${prefix}${subfolder}cache/${baseName}-${targetW}.${ext}`
}

/**
 * Fallback handler for image loading errors.
 * When a cached thumbnail fails to load (404), this handler:
 * Falls back directly to the original full-size image without calling resize API
 *
 * Usage in Vue template: @error="onImageError"
 */
export function onImageError(event) {
    const img = event.target
    if (!img || !img.src) return

    // Prevent infinite error loops
    if (img.dataset.fallbackAttempted === 'original') return

    const src = img.getAttribute('src') || img.src

    // Parse cache path: /uploads/cache/image-xxx-WIDTH.ext or /uploads/subfolder/cache/image-xxx-WIDTH.ext
    const cacheMatch = src.match(/\/uploads\/(?:(.+)\/)?cache\/([^/]+)-(\d+)\.([a-zA-Z0-9]+)$/)
    if (cacheMatch) {
        // Fallback directly to the original full-size image
        const subfolder = cacheMatch[1] || ''
        const baseName = cacheMatch[2]
        const ext = cacheMatch[4]
        const originalPath = subfolder
            ? `/uploads/${subfolder}/${baseName}.${ext}`
            : `/uploads/${baseName}.${ext}`
        img.dataset.fallbackAttempted = 'original'
        img.src = originalPath
        return
    }

    // For non-cache URLs that error, do nothing (prevent infinite loop)
    img.dataset.fallbackAttempted = 'original'
}

/**
 * Compresses and downscales an image File client-side before uploading.
 * Reduces 10MB+ phone photos to ~300KB in milliseconds, preventing upload timeouts.
 *
 * @param {File} file - The original File object
 * @param {Object} [options]
 * @param {number} [options.maxWidth=1920]
 * @param {number} [options.maxHeight=1920]
 * @param {number} [options.quality=0.82]
 * @returns {Promise<File>}
 */
export async function compressImage(file, options = {}) {
    if (!file || !(file instanceof Blob)) return file
    if (!file.type || !file.type.startsWith('image/')) return file

    // Skip SVGs and animated GIFs
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file

    // Skip files already very small (< 250KB)
    if (file.size <= 250 * 1024) return file

    const maxWidth = options.maxWidth || 1920
    const maxHeight = options.maxHeight || 1920
    const quality = options.quality || 0.82

    try {
        let bitmap = null
        let width = 0
        let height = 0

        if (typeof createImageBitmap === 'function') {
            try {
                bitmap = await createImageBitmap(file)
                width = bitmap.width
                height = bitmap.height
            } catch (e) {
                bitmap = null
            }
        }

        let imgElement = null
        if (!bitmap) {
            imgElement = await new Promise((resolve, reject) => {
                const img = new Image()
                const url = URL.createObjectURL(file)
                img.onload = () => {
                    URL.revokeObjectURL(url)
                    resolve(img)
                }
                img.onerror = (err) => {
                    URL.revokeObjectURL(url)
                    reject(err)
                }
                img.src = url
            })
            width = imgElement.naturalWidth || imgElement.width
            height = imgElement.naturalHeight || imgElement.height
        }

        // If image already fits bounds and size is reasonable (< 800KB), return original
        if (width <= maxWidth && height <= maxHeight && file.size <= 800 * 1024) {
            if (bitmap && typeof bitmap.close === 'function') bitmap.close()
            return file
        }

        // Calculate aspect-ratio preserved dimensions
        let targetWidth = width
        let targetHeight = height

        if (targetWidth > maxWidth || targetHeight > maxHeight) {
            const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight)
            targetWidth = Math.round(targetWidth * ratio)
            targetHeight = Math.round(targetHeight * ratio)
        }

        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight

        const ctx = canvas.getContext('2d', { alpha: file.type === 'image/png' })
        if (!ctx) {
            if (bitmap && typeof bitmap.close === 'function') bitmap.close()
            return file
        }

        if (bitmap) {
            ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight)
            if (typeof bitmap.close === 'function') bitmap.close()
        } else if (imgElement) {
            ctx.drawImage(imgElement, 0, 0, targetWidth, targetHeight)
        }

        // Output as WebP if possible, fallback to JPEG
        const outputType = 'image/webp'

        const blob = await new Promise((resolve) => {
            canvas.toBlob((b) => {
                if (b) resolve(b)
                else {
                    // Fallback to JPEG if WebP blob generation fails
                    canvas.toBlob((jb) => resolve(jb), 'image/jpeg', quality)
                }
            }, outputType, quality)
        })

        if (!blob || blob.size >= file.size) {
            return file
        }

        const baseName = (file.name || 'image').replace(/\.[^/.]+$/, '')
        const extension = blob.type === 'image/webp' ? '.webp' : '.jpg'
        return new File([blob], `${baseName}${extension}`, { type: blob.type, lastModified: Date.now() })
    } catch (err) {
        console.warn('Client-side compression skipped:', err)
        return file
    }
}
